/**
 * deaYield.ts — live data for the Katalogos "DEA Yield" tab.
 *
 * Reads the DEA-yield DistributionVault's current pool + a watched set of
 * DEA holder cashaddrs from chipnet, computes per-epoch pro-rata for the
 * connected wallet, and pushes the results into DOM ids the hand-authored
 * app.html declares (`yUserHeld`, `yUserShare`, `yPool`, `yDist`,
 * `yYouEpoch`, `yLast`).
 *
 * v1 posture — watched-set scan. Holders outside the set are invisible to
 * the attributor and to this display; they receive nothing but don't
 * dilute anyone else's slice. v2 = BCMR reverse-index (out of scope).
 *
 * DEA category is the chipnet flagship `Deviant Equilibrium Asset`
 * (`3c2337f5…`) — see snapshot 0258 for the class/asset name split.
 */
import { PLATFORM_ADDRESSES } from './platformFee';
import { ElectrumClient, fetchAddressUtxos, fetchAddressHistory, type AddressUtxo, type HistoryEntry } from './electrum';

export const DEA_CATEGORY = '3c2337f504ad543518040c2d96477e473efaf8803aa010ce63e93c07996fd0c0';
export const VAULT_RESERVE_BITS = 1000n;

/**
 * Watched chipnet DEA holders (v1). Public cashaddrs of the test wallets
 * `.chipnet-keys-{d,f,g}` — each currently holds 3,750 units. Extend as
 * new holders come online; a BCMR indexer replaces this in v2.
 */
export const WATCHED_DEA_HOLDERS: readonly string[] = [
  'bchtest:qzjqnzgjz7w9tdflpz6ufx0rnrnfu4hsps9rznw4dl', // D
  'bchtest:qr4f5sh8lhs50u7xq5568rcqxwk209dz4ynanfnyhh', // F
  'bchtest:qp20fvzqy43q7u43erhjxg52w39mqn7cccyqnlkaqu', // G
];

interface HolderSnapshot { cashaddr: string; heldUnits: bigint; }
interface LastVaultActivity { txHash: string; height: number; }
interface DeaYieldSnapshot {
  userHeld: bigint;
  visibleTotal: bigint;
  userSharePpm: number;      // parts-per-million; UI displays as % with 4 dp
  poolBits: bigint;
  distributableBits: bigint;
  userEntitlementBits: bigint;
  holders: HolderSnapshot[];
  lastActivity: LastVaultActivity | null;
  scannedAt: string;
}

const FETCH_TIMEOUT_MS = 7_000;

/** Race a promise against a timeout — if the socket hangs the whole snapshot
 *  should still resolve (with what it has) instead of leaving the UI on `—`. */
function withTimeout<T>(p: Promise<T>, fallback: T, ms = FETCH_TIMEOUT_MS): Promise<T> {
  return new Promise((resolve) => {
    let done = false;
    const t = setTimeout(() => { if (!done) { done = true; resolve(fallback); } }, ms);
    p.then((v) => { if (!done) { done = true; clearTimeout(t); resolve(v); } })
     .catch(() => { if (!done) { done = true; clearTimeout(t); resolve(fallback); } });
  });
}

async function heldByAddress(client: ElectrumClient, addr: string): Promise<bigint> {
  const utxos = await withTimeout(fetchAddressUtxos(client, addr), [] as AddressUtxo[]);
  let sum = 0n;
  for (const u of utxos) {
    if (u.token_data?.category === DEA_CATEGORY && !u.token_data.nft) {
      sum += BigInt(u.token_data.amount ?? '0');
    }
  }
  return sum;
}

async function bchByAddress(client: ElectrumClient, addr: string): Promise<bigint> {
  const utxos = await withTimeout(fetchAddressUtxos(client, addr), [] as AddressUtxo[]);
  let sum = 0n;
  for (const u of utxos) if (!u.token_data) sum += BigInt(u.value);
  return sum;
}

export async function fetchDeaYieldSnapshot(
  client: ElectrumClient,
  userCashaddr: string | null,
): Promise<DeaYieldSnapshot> {
  const vault = PLATFORM_ADDRESSES.chipnet.deaYieldVaultAddress;
  const holders: HolderSnapshot[] = [];
  const scanSet = new Set<string>(WATCHED_DEA_HOLDERS);
  if (userCashaddr) scanSet.add(userCashaddr);

  const [poolBits, historyRaw, ...heldPerAddr] = await Promise.all([
    bchByAddress(client, vault),
    withTimeout(fetchAddressHistory(client, vault), [] as HistoryEntry[]),
    ...Array.from(scanSet).map((a) => heldByAddress(client, a)),
  ]);
  const history = historyRaw ?? [];
  // Fulcrum returns history chronologically (oldest first). Last entry with
  // a positive height is the most recent confirmed vault activity. We don't
  // parse the tx to distinguish payout vs credit — the label reads "last
  // vault activity" and clicking through to an explorer tells the whole story.
  const confirmed = history.filter((h) => h.height > 0);
  const mostRecent = confirmed.length > 0 ? confirmed[confirmed.length - 1] : null;
  const lastActivity: LastVaultActivity | null = mostRecent ? { txHash: mostRecent.tx_hash, height: mostRecent.height } : null;
  const scanArr = Array.from(scanSet);
  for (let i = 0; i < scanArr.length; i++) {
    const h = heldPerAddr[i];
    if (h > 0n) holders.push({ cashaddr: scanArr[i], heldUnits: h });
  }
  const visibleTotal = holders.reduce((a, h) => a + h.heldUnits, 0n);
  const userHeld = userCashaddr ? (holders.find((h) => h.cashaddr === userCashaddr)?.heldUnits ?? 0n) : 0n;
  const distributableBits = poolBits > VAULT_RESERVE_BITS ? poolBits - VAULT_RESERVE_BITS : 0n;
  const userEntitlementBits = visibleTotal > 0n ? (distributableBits * userHeld) / visibleTotal : 0n;
  const userSharePpm = visibleTotal > 0n ? Number((userHeld * 1_000_000n) / visibleTotal) : 0;
  return { userHeld, visibleTotal, userSharePpm, poolBits, distributableBits, userEntitlementBits, holders, lastActivity, scannedAt: new Date().toISOString() };
}

// ── DOM binding ─────────────────────────────────────────────────────────────

function fmtInt(n: bigint): string { return n.toLocaleString('en-US'); }
function fmtPct(ppm: number): string { return (ppm / 10_000).toFixed(4) + '%'; }
function set(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

/**
 * paintDeaYield — writes the snapshot to the DOM ids in the DEA Yield tab.
 * Preserves the `<span class="u">` suffix on the big-num field.
 */
function shortTxid(h: string): string { return h.slice(0, 12) + '…' + h.slice(-4); }

export function paintDeaYield(snap: DeaYieldSnapshot): void {
  const heldEl = document.getElementById('yUserHeld');
  if (heldEl) heldEl.innerHTML = fmtInt(snap.userHeld) + ' <span class="u">DEA held</span>';
  set('yUserShare', snap.visibleTotal > 0n ? `${fmtPct(snap.userSharePpm)} of ${fmtInt(snap.visibleTotal)} visible` : '—');
  set('yPool', fmtInt(snap.poolBits) + ' bits');
  set('yDist', fmtInt(snap.distributableBits) + ' bits');
  set('yYouEpoch', fmtInt(snap.userEntitlementBits) + ' bits');
  const lastEl = document.getElementById('yLast');
  if (lastEl) {
    if (snap.lastActivity) {
      const s = shortTxid(snap.lastActivity.txHash);
      lastEl.innerHTML = `<span title="${snap.lastActivity.txHash} · block ${snap.lastActivity.height}" style="font-family:var(--mono);cursor:copy" onclick="navigator.clipboard&&navigator.clipboard.writeText('${snap.lastActivity.txHash}')">${s}</span> <span style="opacity:.55">· #${snap.lastActivity.height}</span>`;
    } else {
      lastEl.textContent = '—';
    }
  }
}

/** Called on total-fetch failure — writes a clear "chipnet offline" state
 *  across the live fields so the user isn't stuck reading `—` forever. */
export function paintDeaYieldOffline(): void {
  const heldEl = document.getElementById('yUserHeld');
  if (heldEl) heldEl.innerHTML = '— <span class="u">DEA held</span>';
  set('yUserShare', 'chipnet offline');
  set('yPool', 'chipnet offline');
  set('yDist', 'chipnet offline');
  set('yYouEpoch', 'chipnet offline');
  set('yLast', 'chipnet offline');
}

/**
 * wireDeaYield — attach a refresher to `window.potidaeaDeaYield` and refresh
 * on: (a) initial load, (b) each activation of the yield sub-tab, (c) each
 * successful wallet balance refresh.
 */
export function wireDeaYield(getClient: () => Promise<ElectrumClient | null>, getUserCashaddr: () => string | null): void {
  const refresh = async (): Promise<void> => {
    const p = await getClient();
    if (!p) { paintDeaYieldOffline(); return; }
    try {
      const snap = await fetchDeaYieldSnapshot(p, getUserCashaddr());
      // If every fetch fell through to its timeout, the pool + visibleTotal
      // both come back as 0 with no user cashaddr → indistinguishable from
      // "empty vault, no holders" (the honest live state). Distinguish the
      // two only if there's no meaningful data AND the browser has generic
      // network errors — otherwise show what we have.
      if (snap.poolBits === 0n && snap.visibleTotal === 0n && snap.userHeld === 0n) {
        paintDeaYieldOffline();
      } else {
        paintDeaYield(snap);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[dea-yield] refresh failed', e);
      paintDeaYieldOffline();
    }
  };
  interface Handle { refresh(): Promise<void>; }
  interface Ext { potidaeaDeaYield?: Handle }
  (window as unknown as Ext).potidaeaDeaYield = { refresh };
  document.querySelectorAll('.st[data-kt="yield"]').forEach((b) => {
    b.addEventListener('click', () => { void refresh(); });
  });
  void refresh();
}
